/* PR 58 / 59 FIXES · F6 — the firm dashboard's status chips open /engagements?stage=<stage> (dashboard/fdTheme.js TARGET).
   The Engagements tab reads the param here (kept out of pages/Engagements.jsx):
     - valid values are the five derived stages (fdTheme STAGES — one list); anything else is "no filter" (parseStageParam, the
       one place that decides), never a guessed stage and never shown;
     - a row matches only when its GET /engagements/{id}/figures has loaded and status.stage equals the param. A row still
       loading is NOT shown and NOT counted as a match: the bar says "Checking the status of n engagement(s)…" until it
       loads; a row whose figures failed is not a match (its stage is unknown);
     - the chip "Status: <stage> ✕" (stage label = dsD.stage.*) removes the param; other query params are kept;
     - nothing loading and nothing matching → "No engagements at this stage". */
import React from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { asArray } from "@/lib/asArray";
import { STAGES, fill } from "@/components/dashboard/fdTheme";

export const STAGE_PARAM = "stage";

/** The ?stage= value when it is one of the five stages, otherwise null (= no filter). */
export function parseStageParam(raw) {
  const v = String(raw ?? "").trim();
  return STAGES.includes(v) ? v : null;
}

/** { rows, pending } — rows to show; pending = engagements whose figures are still loading while a filter is set. */
export function filterByStage(engagements, figures, stage) {
  const list = asArray(engagements);
  if (!stage) return { rows: list, pending: 0 };
  let pending = 0;
  const rows = list.filter((e) => {
    const fig = figures?.[e.id];
    if (!fig || fig.loading) { pending += 1; return false; }
    return fig.data?.status?.stage === stage;
  });
  return { rows, pending };
}

export function useStageFilter(engagements, figures) {
  const [params, setParams] = useSearchParams();
  const stage = parseStageParam(params.get(STAGE_PARAM));
  const clear = () => setParams((prev) => { const n = new URLSearchParams(prev); n.delete(STAGE_PARAM); return n; });
  return { stage, clear, ...filterByStage(engagements, figures, stage) };
}

export function StageFilterBar({ stage, rows, pending, clear, t }) {
  if (!stage) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="eng-stage-filter">
      <span className="inline-flex items-center gap-1 rounded-full border bg-slate-50 px-3 py-1 text-xs font-medium dark:bg-slate-900/60"
            data-testid="eng-stage-chip">
        {fill(t("fxD.stageFilter.chip"), { stage: t(`dsD.stage.${stage}`) })}
        <button type="button" onClick={clear} aria-label={t("fxD.stageFilter.clear")} title={t("fxD.stageFilter.clear")}
                className="ms-1 rounded-full text-slate-500 hover:text-slate-900" data-testid="eng-stage-chip-clear">
          <X className="h-3 w-3" />
        </button>
      </span>
      {pending > 0 && (
        <span className="text-xs text-muted-foreground" aria-busy="true" data-testid="eng-stage-pending">
          {fill(t("fxD.stageFilter.loading"), { n: pending })}
        </span>
      )}
      {pending === 0 && rows.length === 0 && (
        <span className="text-muted-foreground" data-testid="eng-stage-empty">{t("fxD.stageFilter.empty")}</span>
      )}
    </div>
  );
}
